import React, { useState, useEffect, useCallback } from 'react';
import { Banners } from 'src/model/Banners';

interface CarouselBannerProps {
  slides: Banners[];
  hideIndicators?: boolean;
  hideControls?: boolean;
  className?: string;
  classNameImg?: string;
  goSlide?: (id: string) => void;
}

export const CarouselBannerUI: React.FC<CarouselBannerProps> = ({
  slides,
  hideIndicators = false,
  hideControls = false,
  className,
  classNameImg,
  goSlide,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showButtons, setShowButtons] = useState(false);

  let touchStartX: number | null = null;
  let touchEndX: number | null = null;

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartX = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    touchEndX = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX !== null && touchEndX !== null) {
      const swipeDistance = touchStartX - touchEndX;

      const minSwipeDistance = 50;

      if (swipeDistance > minSwipeDistance) {
        nextSlide();
      } else if (swipeDistance < -minSwipeDistance) {
        prevSlide();
      }
    }

    touchStartX = null;
    touchEndX = null;
  };

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === slides.length - 1 ? 0 : prevIndex + 1
    );
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? slides.length - 1 : prevIndex - 1
    );
  }, [slides.length]);

  useEffect(() => {
    const interval = setInterval(nextSlide, 8000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  return (
    <div
      className="relative w-full overflow-hidden"
      onMouseEnter={() => setShowButtons(true)}
      onMouseLeave={() => setShowButtons(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className={`relative w-full ${className}`}>
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-transform duration-500 ease-in-out ${
              index === currentIndex ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            {goSlide ? (
              <a href={slide.link} className="cursor-pointer rounded">
                <img
                  src={
                    slide.imageMobile?.includes('/uploads/null')
                      ? slide.image
                      : slide.imageMobile
                  }
                  alt={slide.altText}
                  className="block md:hidden w-full h-full object-cover max-w-full mx-auto"
                />
                <img
                  src={slide.image}
                  alt={slide.altText}
                  className={`hidden md:block w-full h-full object-cover ${classNameImg} max-w-full mx-auto`}
                />
              </a>
            ) : (
              <>
                <img
                  src={
                    slide.imageMobile?.includes('/uploads/null')
                      ? slide.image
                      : slide.imageMobile
                  }
                  alt={slide.altText}
                  className="block md:hidden w-full h-full object-cover max-w-full mx-auto"
                />
                <img
                  src={slide.image}
                  alt={slide.altText}
                  className={`hidden md:block w-full h-full object-cover ${classNameImg} max-w-full mx-auto`}
                />
              </>
            )}
          </div>
        ))}
      </div>

      {!hideIndicators && (
        <div className="absolute z-30 flex space-x-3 bottom-2 left-1/2 transform -translate-x-1/2">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setCurrentIndex(index)}
              className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                index === currentIndex ? 'bg-red-300' : 'bg-gray-400'
              }`}
              aria-label={`Slide ${index + 1}`}
            ></button>
          ))}
        </div>
      )}

      {!hideControls && showButtons && (
        <>
          <button
            type="button"
            className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-white/30 hover:bg-white/50 text-gray-800 rounded-full p-2 shadow-lg md:block"
            onClick={prevSlide}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="sr-only">Previous</span>
          </button>
          <button
            type="button"
            className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-white/30 hover:bg-white/50 text-gray-800 rounded-full p-2 shadow-lg md:block"
            onClick={nextSlide}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <span className="sr-only">Next</span>
          </button>
        </>
      )}
    </div>
  );
};
